import React from "react";
import { FileCheck, ShieldCheck, CalendarClock, Trash2, Scale, Globe } from "lucide-react";
import FeaturePage, { MockWindow } from "../features/FeaturePage";
import { siblingSolutions, DEMO_CTA } from "./links";

const ACCENT = "#0F172A";

// ── Hero visual: consent capture ──
function ConsentVisual() {
  return (
    <MockWindow label="CANDIDATE CONSENT" badge="Recorded" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl border border-border p-3.5 bg-secondary/30 text-[11px] text-muted-foreground leading-relaxed">
          "I consent to the Company collecting and processing my personal information for this application, retained for a maximum of 12 months…"
        </div>
        <div className="flex items-center gap-2.5 rounded-xl border p-3" style={{ borderColor: `${ACCENT}22`, background: `${ACCENT}08` }}>
          <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "#10B98120", color: "#047857" }}>
            <FileCheck className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="text-xs font-semibold text-[#0F172A]">Consent captured · timestamped</div>
            <div className="font-mono text-[10px] text-muted-foreground">Snapshot stored with the candidate record</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["PIPEDA", "Quebec Law 25", "CCPA"].map((l) => (
            <span key={l} className="font-mono text-[9px] px-2 py-1 rounded border" style={{ borderColor: `${ACCENT}22`, color: ACCENT }}>{l}</span>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: retention window ──
function RetentionVisual() {
  return (
    <MockWindow label="DATA RETENTION" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl border border-border p-4 text-center">
          <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em] mb-1">RETENTION PERIOD</div>
          <div className="font-display font-black text-3xl text-[#0F172A]">12<span className="text-base font-bold text-muted-foreground"> months</span></div>
          <div className="text-[11px] text-muted-foreground mt-1">Configurable per your policy</div>
        </div>
        <div className="flex items-center gap-2.5 rounded-xl border p-3" style={{ borderColor: "#E11D4826", background: "#E11D480A" }}>
          <Trash2 className="w-3.5 h-3.5 flex-shrink-0 text-[#E11D48]" />
          <span className="text-[11px] text-muted-foreground leading-snug">After the window, candidate data is securely and permanently deleted — including recordings.</span>
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: candidate rights ──
function RightsVisual() {
  const rights = ["Access their data", "Withdraw consent", "Request deletion", "Correct information"];
  return (
    <MockWindow label="CANDIDATE RIGHTS" accent={ACCENT}>
      <div className="space-y-2">
        {rights.map((r) => (
          <div key={r} className="flex items-center gap-2.5 rounded-xl border border-border p-2.5">
            <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "#10B98120", color: "#047857" }}>
              <ShieldCheck className="w-3 h-3" />
            </div>
            <span className="text-xs text-[#0F172A]">{r}</span>
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

export default function ComplianceSolution() {
  return (
    <FeaturePage
      accent={ACCENT}
      signupRole="company"
      secondaryCta={DEMO_CTA}
      eyebrow="COMPLIANCE & CONSENT"
      title="Hire with AI."
      titleAccent="Stay on the right side of the law."
      subtitle="Capture candidate consent, enforce data-retention limits, and honour privacy rights out of the box — with coverage for PIPEDA, Quebec Law 25, and CCPA, so your AI hiring stays defensible."
      heroChips={["Consent capture", "Data-retention limits", "PIPEDA · Law 25 · CCPA"]}
      heroVisual={<ConsentVisual />}
      rows={[
        {
          tag: "CONSENT CAPTURE",
          title: "Get consent on the record — automatically.",
          body: "Present a customisable consent and privacy notice before an interview and store a timestamped snapshot with the candidate. No loose paperwork, no ambiguity about what was agreed and when.",
          bullets: [
            "Customisable consent template",
            "Timestamped snapshot per candidate",
            "Editable to fit your jurisdiction",
          ],
          visual: <ConsentVisual />,
        },
        {
          tag: "DATA RETENTION",
          title: "Keep data only as long as you should.",
          body: "Set a retention window — 12 months by default — and candidate data, including recordings, is securely deleted when it expires. You hold exactly what your policy allows, and no more.",
          bullets: [
            "Configurable retention period",
            "Automatic, permanent deletion",
            "Covers transcripts and recordings",
          ],
          visual: <RetentionVisual />,
          reverse: true,
        },
        {
          tag: "CANDIDATE RIGHTS",
          title: "Honour the rights candidates are owed.",
          body: "Access, correction, consent withdrawal, and deletion are built into the flow — the rights modern privacy laws require. Deleting a candidate cascades to their recordings and data, so a withdrawal request is genuinely honoured.",
          bullets: [
            "Access, correction & deletion",
            "Withdraw consent at any time",
            "Deletion cascades to recordings",
          ],
          visual: <RightsVisual />,
        },
      ]}
      steps={[
        { title: "Configure once", body: "Set your consent notice and retention window to match your policy." },
        { title: "Capture on every candidate", body: "Consent is recorded and timestamped before each interview." },
        { title: "Retention runs itself", body: "Data is deleted automatically when its window ends, or on request." },
      ]}
      useCases={[
        { icon: Globe, persona: "Multi-region hiring", body: "Coverage for PIPEDA, Quebec Law 25, and CCPA keeps cross-border hiring above board." },
        { icon: Scale, persona: "Regulated industries", body: "Defensible consent and retention records for teams that get audited." },
        { icon: CalendarClock, persona: "Talent-pool builders", body: "Keep candidates only as long as consent and policy allow — automatically." },
      ]}
      faqs={[
        { q: "Which privacy laws does this cover?", a: "The built-in consent notice references PIPEDA, Quebec Law 25, and the CCPA, and the template is fully editable so you can tailor it to your jurisdiction and policies." },
        { q: "How is consent recorded?", a: "Candidates review a consent and privacy notice before interviewing, and a timestamped snapshot of exactly what they agreed to is stored with their record." },
        { q: "What happens to data after the retention period?", a: "When the retention window ends — 12 months by default, and configurable — candidate data is securely and permanently deleted, including interview recordings." },
        { q: "Can a candidate withdraw consent or be deleted?", a: "Yes. Candidate rights including access, correction, withdrawal, and deletion are supported, and deleting a candidate cascades to their recordings and associated data." },
        { q: "Is this legal advice?", a: "No — it's tooling that helps you operate compliantly. You should still confirm your specific obligations with your own legal counsel." },
      ]}
      ctaTitle="Compliant by default."
      ctaSubtitle="Start free, or book a demo to review the consent and retention controls."
      related={siblingSolutions("compliance")}
      seoTitle="Compliant AI Hiring — Consent, Retention & Privacy | VetraHub"
      seoDescription="Capture candidate consent, enforce data-retention limits, and honour privacy rights with built-in coverage for PIPEDA, Quebec Law 25, and CCPA."
    />
  );
}
